/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { type ContextValue, type Key, Provider, ToggleButtonGroup as ToggleButtonGroupCore, type ToggleButtonGroupProps as ToggleButtonGroupCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { SegmentedButtonContext } from '../segmented-button/segmented-button.component';

interface SegmentedButtonGroupProps extends Omit<ToggleButtonGroupCoreProps, 'style' | 'className'> {
  size?: keyof typeof sizeVariants;
  style?: stylex.StyleXStyles;
}

const SegmentedButtonGroupContext = createContext<ContextValue<SegmentedButtonGroupProps, HTMLDivElement>>({});

const SegmentedButtonGroup = forwardRef((props: SegmentedButtonGroupProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, SegmentedButtonGroupContext);

  const { style, children, size = 32, ...coreProps } = props;

  const selectionCounter = useRef(0);
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });

  const updateIndicatorPosition = (selectedKey: Key) => {
    if (!ref.current || !selectedKey) {
      selectionCounter.current = 0;
      return;
    }

    const selectedButton = ref.current.querySelector(`[data-id="${selectedKey}"]`) as HTMLElement;
    if (selectedButton) {
      const containerRect = ref.current.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();
      const left = buttonRect.left - containerRect.left;

      setIndicatorStyle({ width: buttonRect.width, left });

      selectionCounter.current++;
    }
  };

  useLayoutEffect(() => {
    const defaultKey = Array.from(coreProps.defaultSelectedKeys || coreProps.selectedKeys || [])[0];
    if (defaultKey && ref.current) {
      void document.fonts.ready.then(() => {
        updateIndicatorPosition(defaultKey);
      });
    }
    return () => {
      if (defaultKey && ref.current) {
        selectionCounter.current -= 1;
      }
    };
  }, [coreProps.defaultSelectedKeys, coreProps.selectedKeys]);

  return (
    <ToggleButtonGroupCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...coreProps}
      onSelectionChange={(keys) => {
        if (keys.size > 0) {
          updateIndicatorPosition(Array.from(keys)[0]);
          coreProps.onSelectionChange?.(keys);
        }
      }}>
      {(renderProps) => {
        return (
          <Provider values={[
            [SegmentedButtonContext, { size: size }]]
          }>
            <div {...stylex.props(styles.indicator({
              ...indicatorStyle,
              left: renderProps.state.selectedKeys.size === 0 ? indicatorStyle.left : 0,
              width: indicatorStyle.width,
              transform: renderProps.state.selectedKeys.size === 0 ? 0 : indicatorStyle.left,
              opacity: renderProps.state.selectedKeys.size > 0 ? 1 : 0,
              isAnimate: selectionCounter.current > 1
            }))}
            />
            {typeof children === 'function' ? children(renderProps) : children}
          </Provider>
        );
      }}
    </ToggleButtonGroupCore>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.opacity04,
    borderRadius: 999,
    display: 'inline-flex',
    justifyContent: 'center',
    padding: 2,
    position: 'relative',
  },
  indicator: (props: { left: number, width: number, opacity: number, transform: number, isAnimate: boolean }) => ({
    backgroundColor: $.surfaceBGPrimary,
    borderRadius: 999,
    boxShadow: $.shadowBottom040Medium,
    height: 'calc(100% - 4px)',
    left: props.left,
    opacity: props.opacity,
    position: 'absolute',
    top: 2,
    transform: `translateX(${props.transform}px)`,
    // eslint-disable-next-line @stylexjs/valid-styles
    transitionDuration: props.isAnimate ? '0.2s' : '0s',
    transitionProperty: 'transform, width',
    transitionTimingFunction: 'ease-in-out',
    width: props.width,
    zIndex: 1,
  }),
});

const sizeVariants = stylex.create({
  28: {
    height: 28,
  },
  32: {
    height: 32,
  },
  40: {
    height: 40,
  }
});

export { SegmentedButtonGroup, SegmentedButtonGroupContext, type SegmentedButtonGroupProps };

